import { useContext, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { notification } from "antd";

import { useGetAssessmentInviteHook } from "@/modules/assessments/hooks/use-get-assessment-invite.hook";
import { useGetCandidateAnswersHook } from "@/modules/assessments/hooks/use-get-candidate-answers.hook";
import { useGetAssessmentCompositesByInviteHook } from "@/modules/assessments/hooks/use-get-assessment-composites-by-invite.hook";
import { useGetInviteScaleAnalysesHook } from "@/modules/assessments/hooks/use-get-invite-scale-analyses.hook";
import { useGetInviteScaleDevelopmentHook } from "@/modules/assessments/hooks/use-get-invite-scale-development.hook";
import { useClientByIdHook } from "@/modules/client/hooks/useClientById.hook";
import { DEFAULT_LOCALE } from "@/i18n";

import { Loading } from "@/shared/components/Loading/Loading";
import { ReportComponent, ReportScene } from "@/shared/components/assessment/report/ReportComponent/ReportComponent";
import { AssessmentAbilityContext } from "@/shared/contexts/ability.context";
import { AssessmentContext } from "@/shared/contexts/assessment.context";
import { useGetUserHook } from "@/shared/hooks/users/use-get-user.hook";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { maybe } from "@/shared/utils/formaters/dataFormater/commonFormatter";
import { shortFormatDate } from "@/shared/utils/formaters/dataFormater/formatDate";
import { resolveEffectiveLocales } from "@/shared/utils/locale.utils";

import { ASSESSMENT_ROUTE, CLIENT_ROUTE } from "@/routing/AppRouter/routes.constants";
import { SEO } from "@/ui-components/commonComponents/SEO/SEO";

export default function AssessmentReport() {
  const { invite_id, client_id } = useParams();

  const { assessment } = useContext(AssessmentContext);
  const assessmentAbility = useContext(AssessmentAbilityContext);
  const canViewReports = assessmentAbility.can(Action.Read, Subject.ASSESSMENT_INVITE, "reports");

  const { client } = useClientByIdHook(Number(client_id));
  const { assessmentInvite, isLoading: inviteLoading } = useGetAssessmentInviteHook(Number(invite_id));
  const { user, isLoading: isLoadingUser } = useGetUserHook(assessmentInvite?.claimedBy);
  const { answers, isLoading: answersLoading } = useGetCandidateAnswersHook(Number(invite_id));
  const hasTemplate = Boolean(assessment?.template);
  const { composites, isLoading: isCompositesLoading } = useGetAssessmentCompositesByInviteHook({
    inviteId: Number(invite_id),
    hasTemplate,
    includeInterviewGuide: Boolean(assessment?.showInterviewGuide),
    inviteStatus: assessmentInvite?.status,
  });
  const { scaleAnalyses, isLoading: isScaleAnalysisLoading } = useGetInviteScaleAnalysesHook(
    Number(invite_id),
    hasTemplate,
    assessmentInvite?.status,
  );
  const showScaleDevelopment = assessment?.showScaleDevelopment ?? false;
  // Develop has its own display language (defaults to English, separate from the global app locale) so
  // switching it re-renders only the Develop report.
  const [developmentLocale, setDevelopmentLocale] = useState(DEFAULT_LOCALE);

  const {
    developmentSequences,
    availableLocales: developmentLocales,
    isInitialLoading: isScaleDevelopmentInitialLoading,
  } = useGetInviteScaleDevelopmentHook({
    inviteId: Number(invite_id),
    shouldLoadScaleDevelopment: showScaleDevelopment,
    inviteStatus: assessmentInvite?.status,
    locale: developmentLocale,
  });

  if (
    inviteLoading ||
    answersLoading ||
    !client ||
    !assessment ||
    !assessmentInvite ||
    isLoadingUser ||
    (hasTemplate && (isCompositesLoading || isScaleAnalysisLoading)) ||
    // Only gate the page on the initial Develop load. Language-switch re-fetches keep the prior content
    // mounted, so switching language re-renders just the Develop report instead of unmounting the
    // whole page and bouncing the user off the tab.
    (showScaleDevelopment && isScaleDevelopmentInitialLoading)
  ) {
    return <Loading />;
  }

  if (!canViewReports) {
    notification.error({
      // key is used to prevent duplicate notifications
      key: "no-report-access",
      message: "You don't have permission to view reports for this assessment.",
    });
    return (
      <Navigate to={`/${CLIENT_ROUTE}/${client_id}/${ASSESSMENT_ROUTE}/${assessment.assessmentId}`} replace={true} />
    );
  }

  // Offer the assessment's enabled locales that have Develop content, plus en-US always — the Develop
  // report is admin-facing and must stay viewable in English even when the assessment doesn't enable it.
  const enabledLocales = resolveEffectiveLocales(assessment.enabledLocales);
  const offeredDevelopmentLocales = developmentLocales?.filter(
    (locale) => locale === DEFAULT_LOCALE || enabledLocales.includes(locale),
  );

  return (
    <>
      <SEO
        titleParams={[
          user?.fullName,
          assessment?.name[DEFAULT_LOCALE],
          maybe(assessmentInvite?.finishedOn, shortFormatDate, ""),
        ]}
      />
      <ReportComponent
        user={user}
        client={client}
        assessment={assessment}
        invite={assessmentInvite}
        inviteId={Number(invite_id)}
        usersAnswers={[{ inviteId: Number(invite_id), answers }]}
        scene={ReportScene.single}
        selectedUsers={[String(user?.fullName)]}
        templateComposites={composites}
        scaleAnalyses={scaleAnalyses}
        developmentSequences={developmentSequences}
        developmentLocales={offeredDevelopmentLocales}
        developmentLocale={developmentLocale}
        onDevelopmentLocaleChange={setDevelopmentLocale}
      />
    </>
  );
}
